import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Handles selecting/deselecting tags, updating bulk bar, and clearing state after turbo updates
export default class extends Controller {
  static targets = ["count", "inlineCount", "inlineValue", "bulkBar"]

  connect() {
    // Bind handlers so we can remove them on disconnect
    this._onTurboLoad = () => this.refresh()
    this._onTurboRender = () => this.refresh()
    this._onBeforeStreamRender = (e) => {
      // If tags table is replaced, clear selection
      if (e.target?.getAttribute('target') === 'tags_table_wrapper') {
        this.clearSelection()
      }
    }
    this._onBeforeCache = () => {
      // Prevent stale selection persisting via Turbo Drive cache
      this.clearSelection()
      // Also hide bars explicitly to avoid flicker on back/forward navigation
      if (this.hasBulkBarTarget) this.bulkBarTarget.style.display = 'none'
      if (this.hasInlineCountTarget) this.inlineCountTarget.style.display = 'none'
      this.removeHiddenFields()
    }

    // Recompute after Turbo navigations/updates
    document.addEventListener('turbo:load', this._onTurboLoad)
    document.addEventListener('turbo:render', this._onTurboRender)
    document.addEventListener('turbo:before-stream-render', this._onBeforeStreamRender)
    document.addEventListener('turbo:before-cache', this._onBeforeCache)

    this.refresh() // initialize visibility/state
  }

  disconnect() {
    document.removeEventListener('turbo:load', this._onTurboLoad)
    document.removeEventListener('turbo:render', this._onTurboRender)
    document.removeEventListener('turbo:before-stream-render', this._onBeforeStreamRender)
    document.removeEventListener('turbo:before-cache', this._onBeforeCache)
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.tagCheckboxes().forEach(cb => cb.checked = checked)
    this.refresh()
  }

  checkboxChanged() {
    this.refresh()
  }

  clear(event) {
    if (event) event.preventDefault()
    this.clearSelection()
    this.refresh()
  }

  clearSelection() {
    this.tagCheckboxes().forEach(cb => cb.checked = false)
    const master = document.getElementById('selectAllInner') || document.getElementById('selectAll')
    if (master) master.checked = false
  }

  refresh() {
    const selected = this.tagCheckboxes().filter(cb => cb.checked)
    const count = selected.length
    if (count > 0) {
      this.bulkBarTarget.style.display = 'flex'
      this.inlineCountTarget.style.display = 'inline-flex'
      this.countTarget.textContent = t('javascript.controllers.tag_selection_controller.items_selected', { count })
      this.inlineValueTarget.textContent = count
      this.injectHiddenFields(selected)
    } else {
      this.bulkBarTarget.style.display = 'none'
      this.inlineCountTarget.style.display = 'none'
      this.removeHiddenFields()
    }
  }

  tagCheckboxes() {
    // Limit to the currently visible tags view (desktop table OR mobile cards)
    const wrapper = document.getElementById('tags_table_wrapper') || this.element || document

    // Identify desktop vs mobile containers rendered together in the DOM
    // Desktop container uses classes: "hidden lg:block"; Mobile uses: "lg:hidden"
    const desktop = wrapper.querySelector('.hidden.lg\\:block')
    const mobile = wrapper.querySelector('.lg\\:hidden')

    const visibleRoot = this.visibleContainer(desktop, mobile) || wrapper
    const all = Array.from(visibleRoot.querySelectorAll('input[name="tag_ids[]"]'))
    return all.filter(cb => this.isVisible(cb))
  }

  visibleContainer(desktop, mobile) {
    const isShown = (el) => {
      if (!el) return false
      const style = window.getComputedStyle(el)
      return style.display !== 'none' && style.visibility !== 'hidden'
    }
    if (isShown(desktop)) return desktop
    if (isShown(mobile)) return mobile
    return null
  }

  isVisible(el) {
    // Check visibility up the ancestor chain using computed styles
    if (!el) return false
    let cur = el
    while (cur) {
      const style = window.getComputedStyle(cur)
      if (style.display === 'none' || style.visibility === 'hidden') return false
      cur = cur.parentElement
    }
    const rect = el.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  injectHiddenFields(selected) {
    let holder = document.getElementById('bulk_tag_ids_holder')
    if (!holder) return
    holder.innerHTML = ''
    selected.forEach(cb => {
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = 'tag_ids[]'
      hidden.value = cb.value
      holder.appendChild(hidden)
    })
  }

  removeHiddenFields() {
    let holder = document.getElementById('bulk_tag_ids_holder')
    if (holder) holder.innerHTML = ''
  }
}
